/** @jsxImportSource @emotion/react */
import { useMemo } from 'react'
import ReactSelect from 'react-select'
import { toast } from 'react-toastify'

import {
  useGetPhysicianGroupSettingsQuery,
  useUpdatePhysicianGroupSettingsMutation,
} from 'store/services/physician'
import { useGetLabOfficesQuery } from 'store/services/office'

interface IProps {
  physicianGroupId: number
}

type OfficeOption = {
  value: number | 'none' | 'separator'
  label: string
  isDisabled?: boolean
}

const NONE_OPTION: OfficeOption = { value: 'none', label: 'None' }
const SEPARATOR: OfficeOption = { value: 'separator' as any, label: '─────────────────', isDisabled: true }

export const GroupPreferredDmeOfficeSection = ({ physicianGroupId }: IProps) => {
  const { data: offices = [], isLoading: isLoadingOffices } = useGetLabOfficesQuery({})
  const { data: groupSettings } = useGetPhysicianGroupSettingsQuery({ id: physicianGroupId })
  const [updatePhysicianGroupSettings, { isLoading: isUpdating }] =
    useUpdatePhysicianGroupSettingsMutation()

  const officeOptions = useMemo<OfficeOption[]>(
    () =>
      offices
        .filter((office) => office.isDmeOffice && office.officeName)
        .sort((left, right) => String(left.officeName).localeCompare(String(right.officeName)))
        .map((office) => ({
          value: office.id,
          label: `${office.officeName} (${office.addressCity}, ${office.addressState})`,
        })),
    [offices],
  )

  const allOptions: OfficeOption[] = [NONE_OPTION, SEPARATOR, ...officeOptions]

  const selectedValue: OfficeOption | null = groupSettings?.isPreferredDmeNone
    ? NONE_OPTION
    : officeOptions.find(
        (option) => Number(option.value) === Number(groupSettings?.preferredDmeOfficeId),
      ) ?? null

  const handlePreferredDmeChange = async (option: OfficeOption | null) => {
    const isNone = option?.value === 'none'
    try {
      await updatePhysicianGroupSettings({
        id: physicianGroupId,
        settings: groupSettings?.settings || undefined,
        preferredDeviceTypes: groupSettings?.preferredDeviceTypes || undefined,
        preferredDmeOfficeId: isNone ? null : (option?.value as number) ?? null,
        isPreferredDmeNone: isNone,
      }).unwrap()
      toast.success(
        isNone
          ? 'Group preferred DME set to None.'
          : option
            ? 'Group preferred DME office updated.'
            : 'Group preferred DME office cleared.',
      )
    } catch (error) {
      console.error('Error updating group preferred DME office:', error)
      toast.error('Failed to update group preferred DME office.')
    }
  }

  return (
    <div className="form-group textGray mt-3">
      <label htmlFor="groupPreferredDmeOffice">Preferred DME Office</label>
      <ReactSelect
        inputId="groupPreferredDmeOffice"
        className="basic-single"
        classNamePrefix="select"
        isClearable
        isSearchable
        isLoading={isLoadingOffices || isUpdating}
        placeholder="Search DME offices..."
        value={selectedValue}
        onChange={(option) => handlePreferredDmeChange(option as OfficeOption | null)}
        options={allOptions}
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
      />
    </div>
  )
}
